<template>
  <c-modal
    ref="modalRef"
    :title="isEdit ? 'Categorie bewerken' : 'Nieuwe categorie'"
    size="sm"
    :persistent="false"
  >
    <c-form
      ref="categoryFormRef"
      class="tw-space-y-5"
      @submit.prevent="onSubmit"
    >
      <c-input
        v-model="title"
        autofocus
        name="title"
        label="Naam"
        :rules="titleRules"
        hide-bottom-space
      />

      <div>
        <div class="tw-mb-2 tw-font-medium">Modules</div>
        <div
          v-if="modulesLoading"
          class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600"
        >
          <q-spinner size="16px" />
          Modules laden…
        </div>
        <div v-if="!modulesLoading" class="tw-flex tw-flex-col tw-gap-1">
          <c-checkbox
            v-for="option in moduleOptions"
            :key="option.id"
            borderless
            :model-value="selectedModuleIds.includes(option.id)"
            :label="option.label"
            @update:model-value="(val) => toggleModule(option.id, val)"
          />
        </div>
        <div v-if="showModuleError" class="tw-mt-1 tw-text-sm tw-text-negative">
          Selecteer minstens één module.
        </div>
      </div>
    </c-form>

    <template #actions>
      <c-button
        color="transparent"
        text-color="gray"
        label="Annuleren"
        @click="onCancel"
      />
      <c-button
        color="primary"
        :label="isEdit ? 'Opslaan' : 'Aanmaken'"
        :disable="modulesLoading"
        @click="onSubmit"
      />
    </template>
  </c-modal>
</template>

<script setup lang="ts">
import { useKnowledgeBaseModuleOptions } from "composables/knowledge-base/useKnowledgeBaseModuleOptions";
import type { KnowledgeBaseCategory } from "models/knowledge-base/knowledge-base";
import { computed, onMounted, ref, useTemplateRef } from "vue";

interface Props {
  category?: KnowledgeBaseCategory | null;
}

const props = withDefaults(defineProps<Props>(), {
  category: null,
});

const modalRef = useTemplateRef<{
  dialogRef: { hide: () => void };
  onDialogOK: (payload: unknown) => void;
}>("modalRef");
const categoryFormRef = useTemplateRef<{
  formRef?: { validate: () => Promise<boolean> };
}>("categoryFormRef");

const { moduleOptions, loading, fetchModuleOptions } =
  useKnowledgeBaseModuleOptions();

onMounted(() => {
  void fetchModuleOptions().catch(() => {
    // Foutmelding wordt al getoond in useKnowledgeBaseModuleOptions.
  });
});

const isEdit = computed(() => props.category !== null);

const modulesLoading = computed(
  () => loading.value && moduleOptions.value.length === 0,
);

const title = ref(props.category?.title ?? "");
const selectedModuleIds = ref<number[]>([
  ...(props.category?.module_ids ?? []),
]);
const submitted = ref(false);

const showModuleError = computed(
  () => submitted.value && selectedModuleIds.value.length === 0,
);

const titleRules = [
  (v: string) => !!v || "Naam is verplicht",
  (v: string) => v.length >= 5 || "Minimaal 5 tekens",
  (v: string) => v.length <= 255 || "Maximaal 255 tekens",
];

const toggleModule = (moduleId: number, value: boolean) => {
  if (value) {
    if (!selectedModuleIds.value.includes(moduleId)) {
      selectedModuleIds.value = [...selectedModuleIds.value, moduleId];
    }
  } else {
    selectedModuleIds.value = selectedModuleIds.value.filter(
      (id) => id !== moduleId,
    );
  }
};

const onCancel = (): void => {
  modalRef.value?.dialogRef?.hide();
};

const onSubmit = async (): Promise<void> => {
  submitted.value = true;

  const isValid = await categoryFormRef.value?.formRef?.validate();
  if (isValid !== true) {
    return;
  }

  if (selectedModuleIds.value.length === 0) {
    return;
  }

  modalRef.value?.onDialogOK({
    title: title.value.trim(),
    module_ids: [...selectedModuleIds.value].sort((a, b) => a - b),
  });
};
</script>
